import React from 'react';
import { Link } from 'react-router-dom';
import { FaLock, FaArrowLeft, FaFileContract, FaCheckCircle, FaBan, FaCreditCard, FaExclamationTriangle } from 'react-icons/fa';
import SEO from '../components/SEO';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms of Service - Usage Agreement"
        description="MirageDocs Terms of Service. Understand your rights and responsibilities when using our secure file sharing service. Fair usage, pricing, and legal terms."
        url="https://miragedocs.com/terms"
      />
      {/* Header */}
      <header className="w-full py-6 px-6 bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaLock className="text-[#2186EB]" />
            MirageDocs
          </Link>
          <Link to="/" className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
            <FaArrowLeft /> Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FaFileContract className="text-4xl text-[#2186EB]" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Terms of Service</h1>
          </div>

          <p className="text-gray-600 mb-8">
            Last updated: December 14, 2025
          </p>

          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing or using MirageDocs ("the Service"), you agree to be bound by these Terms of Service.
                If you do not agree to these terms, do not use the Service.
              </p>
              <p className="text-gray-700">
                MirageDocs is a privacy-focused, end-to-end encrypted file sharing service. By using our Service,
                you acknowledge that you understand and accept our zero-knowledge architecture.
              </p>
            </section>

            {/* Service Description */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-700 mb-4">
                MirageDocs provides:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>End-to-end encrypted file sharing with self-destructing links</li>
                <li>Password protection for shared files using SRP protocol</li>
                <li>Intake portals for receiving files from others</li>
                <li>Optional email notifications for file access events</li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-yellow-800">
                  <strong>Important:</strong> Due to our zero-knowledge architecture, we cannot recover your files
                  or encryption keys. If you lose access to a share link, the file cannot be recovered.
                </p>
              </div>
            </section>

            {/* Acceptable Use */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaCheckCircle className="text-[#2186EB]" />
                3. Acceptable Use
              </h2>
              <p className="text-gray-700 mb-4">
                You agree to use MirageDocs only for lawful purposes. You may use the Service to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Share documents securely with colleagues, clients, or partners</li>
                <li>Transfer sensitive business or personal files</li>
                <li>Receive files through intake portals</li>
                <li>Any other lawful file sharing purposes</li>
              </ul>
            </section>

            {/* Prohibited Use */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaBan className="text-red-500" />
                4. Prohibited Use
              </h2>
              <p className="text-gray-700 mb-4">
                You may NOT use MirageDocs to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Share illegal content, including but not limited to child exploitation material</li>
                <li>Distribute malware, viruses, or other harmful software</li>
                <li>Share copyrighted material without authorization</li>
                <li>Engage in harassment, threats, or intimidation</li>
                <li>Conduct or facilitate any illegal activities</li>
                <li>Attempt to circumvent security measures or access other users' data</li>
                <li>Use automated tools to abuse the service (excessive uploads, scraping, etc.)</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Violation of these terms may result in immediate account termination and reporting to authorities.
              </p>
            </section>

            {/* Plans and Payment */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaCreditCard className="text-[#2186EB]" />
                5. Plans and Payment
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Free Tier</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>10 shares per month</li>
                <li>30MB maximum file size</li>
                <li>24-hour maximum expiry</li>
                <li>5 downloads per file</li>
                <li>1 intake portal</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Paid Plans (Pro, Team)</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Payment is processed securely through Stripe</li>
                <li>Subscriptions are billed monthly</li>
                <li>You may cancel at any time; access continues until the billing period ends</li>
                <li>Refunds are handled on a case-by-case basis within 14 days of purchase</li>
                <li>Team plans require a minimum of 2 users</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Plan Changes</h3>
              <p className="text-gray-700">
                You may upgrade or downgrade your plan at any time. Upgrades take effect immediately;
                downgrades take effect at the next billing cycle.
              </p>
            </section>

            {/* Intellectual Property */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                <strong>Your Content:</strong> You retain all rights to the files you upload. We do not claim
                any ownership over your content.
              </p>
              <p className="text-gray-700">
                <strong>Our Service:</strong> MirageDocs, including its design, features, and code, is owned by us.
                You may not copy, modify, or reverse engineer our Service.
              </p>
            </section>

            {/* Disclaimer */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaExclamationTriangle className="text-yellow-500" />
                7. Disclaimer of Warranties
              </h2>
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700 mb-4">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                  EXPRESS OR IMPLIED.
                </p>
                <p className="text-gray-700 mb-4">
                  We do not warrant that:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>The Service will be uninterrupted or error-free</li>
                  <li>Files will be available until their expiry date in all circumstances</li>
                  <li>The Service will meet your specific requirements</li>
                </ul>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, MirageDocs shall not be liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Loss of data due to system failures, user error, or any other cause</li>
                <li>Indirect, incidental, or consequential damages</li>
                <li>Lost profits or business interruption</li>
                <li>Third-party actions or content</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
              <p className="text-gray-700 mb-4">
                <strong>By You:</strong> You may terminate your account at any time through your dashboard
                or by contacting support.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>By Us:</strong> We may terminate or suspend your account immediately if you violate
                these Terms, engage in prohibited activities, or if required by law.
              </p>
              <p className="text-gray-700">
                Upon termination, your data will be deleted according to our Privacy Policy.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-700">
                These Terms are governed by the laws of Luxembourg. Any disputes shall be resolved
                in the courts of Luxembourg City, Luxembourg.
              </p>
            </section>

            {/* Changes */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700">
                We may update these Terms from time to time. We will notify registered users of significant
                changes via email. Continued use of the Service after changes constitutes acceptance of
                the new Terms.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact</h2>
              <p className="text-gray-700">
                For questions about these Terms:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Email:</strong> <a href="mailto:legal@miragedocs.com" className="text-[#2186EB] hover:underline">legal@miragedocs.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900 text-gray-400">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-white mb-4">
            <FaLock />
            <span className="font-bold">MirageDocs</span>
          </div>
          <div className="flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/security" className="hover:text-white">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
